import { useCallback, useEffect, useState } from 'react';
import {
  clearWebNotifications,
  loadWebNotifications,
  subscribeToWebNotifications,
  type WebNotification,
} from '../services/portal';

function formatTimeAgo(value: string): string {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return '';
  const delta = Math.max(0, Date.now() - time);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (delta < minute) return 'Just now';
  if (delta < hour) return `${Math.floor(delta / minute)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  if (delta < 7 * day) return `${Math.floor(delta / day)}d ago`;
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function notificationPresentation(notification: WebNotification) {
  const eventName = notification.event?.name ?? 'an event';
  const actorName = notification.actor?.full_name ?? 'Someone';
  switch (notification.type) {
    case 'join_request': return { icon: '🙋', text: `${actorName} wants to join “${eventName}”.` };
    case 'request_approved': return { icon: '🎉', text: `Your request to join “${eventName}” was approved.` };
    case 'request_denied': return { icon: '•', text: `Your request to join “${eventName}” was not approved.` };
    case 'new_member': return { icon: '👋', text: `${actorName} joined “${eventName}”.` };
    case 'video_ready': return { icon: '🎬', text: notification.body || `Your video for “${eventName}” is ready.` };
    case 'video_processing_failed': return { icon: '⚠️', text: notification.body || `A video for “${eventName}” could not be processed.` };
    case 'save_the_date': return { icon: '💌', text: notification.body || `Save the date for “${eventName}”.` };
    case 'event_reminder': return { icon: '⏰', text: notification.body || `Reminder for “${eventName}”.` };
    case 'join_reminder': return { icon: '🎟️', text: notification.body || `Join “${eventName}” in Gathrd.` };
    case 'upload_reminder': return { icon: '📸', text: notification.body || `Share your photos from “${eventName}”.` };
    case 'storage_billing_14d': return { icon: '💳', text: `Storage billing for “${eventName}” starts in 14 days.` };
    case 'storage_billing_7d': return { icon: '💳', text: `Storage billing for “${eventName}” starts in 7 days.` };
    case 'storage_billing_1d': return { icon: '💳', text: `Storage billing for “${eventName}” starts tomorrow.` };
    case 'storage_billing_started': return { icon: '✓', text: `Storage billing is active for “${eventName}”.` };
    case 'storage_billing_failed':
    case 'storage_expired': return { icon: '⚠️', text: `Storage billing needs attention for “${eventName}”.` };
    case 'participant_limit_reached': return { icon: '👥', text: notification.body || `“${eventName}” reached its participant limit.` };
    case 'invite_limit_reached': return { icon: '↑', text: notification.body || `“${eventName}” reached its invite or guest limit.` };
    case 'content_report': return { icon: '🚩', text: notification.body || `Content was reported in “${eventName}”.` };
    case 'safety_incident': return { icon: '🛡️', text: notification.body || notification.title || 'A Trust & Safety incident needs review.' };
    default: return { icon: '🔔', text: notification.body || notification.title || 'You have a new notification.' };
  }
}

type NotificationsPanelProps = {
  userId: string;
  onOpenEvent: (eventId: string) => void;
};

export function NotificationsPanel({ userId, onOpenEvent }: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState<WebNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setNotifications(await loadWebNotifications(userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load notifications.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
    return subscribeToWebNotifications(userId, () => void refresh());
  }, [refresh, userId]);

  async function handleClear() {
    setBusy(true);
    setError(null);
    try {
      await clearWebNotifications(userId);
      setNotifications([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not clear notifications.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="portal-content-card notifications-card">
      <div className="portal-toolbar-row">
        <div className="portal-section-copy">
          <p className="eyebrow">Notifications</p>
          <h2>What changed while you were away.</h2>
          <p>Join requests, approvals, reminders, storage notices, and event activity use the same notification records as mobile.</p>
        </div>
        {notifications.length > 0 && (
          <button className="secondary-button" type="button" onClick={() => void handleClear()} disabled={busy}>Clear all</button>
        )}
      </div>

      {error && <p className="error-banner portal-inline-error">{error}</p>}
      {loading ? (
        <div className="portal-empty">Loading notifications…</div>
      ) : notifications.length === 0 ? (
        <div className="portal-empty"><strong>All caught up.</strong><span>New Gathrd activity will appear here.</span></div>
      ) : (
        <div className="notification-list">
          {notifications.map((notification) => {
            const presentation = notificationPresentation(notification);
            const eventId = notification.event_id || String((notification.data ?? {}).eventId ?? (notification.data ?? {}).event_id ?? '');
            return (
              <button
                className={`notification-row ${notification.read ? '' : 'notification-unread'}`}
                key={notification.id}
                type="button"
                onClick={() => eventId && onOpenEvent(eventId)}
                disabled={!eventId}
              >
                <span className="notification-icon">{presentation.icon}</span>
                <span className="notification-copy"><strong>{presentation.text}</strong><small>{formatTimeAgo(notification.created_at)}</small></span>
                {eventId && <span className="notification-arrow">→</span>}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
